import React, { useState, useEffect } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { User, Mail, Phone, Car, CreditCard, Save, X, Loader2, CheckCircle, Camera, Upload, Image } from 'lucide-react';
import { toast } from '../hooks/use-toast';
import axios from 'axios';

const DriverProfileEdit = ({ isOpen, onClose, driverId, backendUrl, onUpdate }) => {
  const [profile, setProfile] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isUploadingFront, setIsUploadingFront] = useState(false);
  const [isUploadingRear, setIsUploadingRear] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    paypal_email: '',
    vehicle_type: '',
    vehicle_model: '',
    vehicle_year: '',
    license_plate: '',
    ein_ssn: '',
  });
  const [vehiclePhotos, setVehiclePhotos] = useState({
    front: null,
    rear: null
  });
  const [licensePhoto, setLicensePhoto] = useState(null);
  const [isUploadingLicense, setIsUploadingLicense] = useState(false);

  useEffect(() => {
    if (isOpen && driverId) {
      fetchProfile();
    }
  }, [isOpen, driverId]);

  const fetchProfile = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${backendUrl}/api/drivers/${driverId}/profile`);
      const driverData = response.data.driver || response.data;
      setProfile(driverData);
      setFormData({
        name: driverData.name || '',
        phone: driverData.phone || '',
        email: driverData.email || '',
        paypal_email: driverData.paypal_email || '',
        vehicle_type: driverData.vehicle_type || '',
        vehicle_model: driverData.vehicle_model || '',
        vehicle_year: driverData.vehicle_year || '',
        license_plate: driverData.license_plate || '',
        ein_ssn: '', // Don't pre-fill - driver must enter new value to update
      });
      setVehiclePhotos({
        front: driverData.vehicle_front_photo || null,
        rear: driverData.vehicle_rear_photo || null
      });
      setLicensePhoto(driverData.license_photo || null);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to load profile",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleUploadVehiclePhoto = async (type, file) => {
    if (!file) return;
    
    const setUploading = type === 'front' ? setIsUploadingFront : setIsUploadingRear;
    setUploading(true);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const endpoint = type === 'front' ? 'vehicle-front' : 'vehicle-rear';
      const response = await axios.post(
        `${backendUrl}/api/drivers/${driverId}/upload/${endpoint}`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      
      setVehiclePhotos(prev => ({
        ...prev,
        [type]: response.data.url
      }));
      
      toast({
        title: "Photo Uploaded",
        description: `Vehicle ${type} photo updated successfully`,
      });
    } catch (error) {
      toast({
        title: "Upload Failed",
        description: error.response?.data?.detail || "Failed to upload photo",
        variant: "destructive"
      });
    } finally {
      setUploading(false);
    }
  };

  const handleUploadLicensePhoto = async (file) => {
    if (!file) return;
    
    setIsUploadingLicense(true);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(
        `${backendUrl}/api/drivers/${driverId}/upload/license-photo`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      
      setLicensePhoto(response.data.url);
      
      toast({
        title: "License Photo Uploaded",
        description: "Your driver's license photo has been saved",
      });
    } catch (error) {
      toast({
        title: "Upload Failed",
        description: error.response?.data?.detail || "Failed to upload license photo",
        variant: "destructive"
      });
    } finally {
      setIsUploadingLicense(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      // Only send changed fields
      const updates = {};
      Object.keys(formData).forEach(key => {
        if (formData[key] !== (profile[key] || '')) {
          updates[key] = formData[key];
        }
      });

      if (Object.keys(updates).length === 0) {
        toast({
          title: "No Changes",
          description: "No fields were modified",
        });
        setIsSaving(false);
        return;
      }

      await axios.put(`${backendUrl}/api/drivers/${driverId}/profile`, updates);
      
      toast({
        title: "Profile Updated!",
        description: "Your changes have been saved successfully",
      });

      // Update localStorage with new values
      if (updates.name) localStorage.setItem('driver_name', updates.name);
      if (updates.email) localStorage.setItem('driver_email', updates.email);
      if (updates.paypal_email) localStorage.setItem('driver_paypal_email', updates.paypal_email);

      if (onUpdate) onUpdate();
      onClose();
    } catch (error) {
      toast({
        title: "Update Failed",
        description: error.response?.data?.detail || "Failed to save changes",
        variant: "destructive"
      });
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[100] flex items-center justify-center p-4">
      <Card className="w-full max-w-lg p-6 relative animate-in fade-in zoom-in-95 max-h-[90vh] overflow-y-auto">
        <button 
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
        >
          <X size={20} />
        </button>
        
        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
            <User className="text-blue-600" size={24} />
          </div>
          <div>
            <h2 className="text-xl font-bold text-gray-900">Edit Profile</h2>
            <p className="text-sm text-gray-500">Update your driver information</p>
          </div>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="animate-spin text-amber-500" size={32} />
          </div>
        ) : (
          <div className="space-y-4">
            {/* Profile Photo */}
            {profile?.profile_photo && (
              <div className="flex justify-center mb-4">
                <div className="relative">
                  <img 
                    src={profile.profile_photo} 
                    alt="Profile" 
                    className="w-20 h-20 rounded-full object-cover border-4 border-amber-200"
                  />
                  <div className="absolute bottom-0 right-0 w-6 h-6 bg-green-500 rounded-full flex items-center justify-center border-2 border-white">
                    <CheckCircle size={12} className="text-white" />
                  </div>
                </div>
              </div>
            )}

            {/* Personal Info */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label className="flex items-center gap-2 text-gray-700 mb-1.5">
                  <User size={14} /> Full Name
                </Label>
                <Input
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="John Doe"
                  className="border-2 focus:border-amber-500"
                />
              </div>
              <div>
                <Label className="flex items-center gap-2 text-gray-700 mb-1.5">
                  <Phone size={14} /> Phone
                </Label>
                <Input
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="(555) 123-4567"
                  className="border-2 focus:border-amber-500"
                />
              </div>
            </div>

            <div>
              <Label className="flex items-center gap-2 text-gray-700 mb-1.5">
                <Mail size={14} /> Email
              </Label>
              <Input
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="driver@example.com"
                className="border-2 focus:border-amber-500"
              />
            </div>

            <div>
              <Label className="flex items-center gap-2 text-gray-700 mb-1.5">
                <CreditCard size={14} /> PayPal Email (for payouts)
              </Label>
              <Input
                name="paypal_email"
                type="email"
                value={formData.paypal_email}
                onChange={handleChange}
                placeholder="paypal@example.com"
                className="border-2 focus:border-amber-500"
              />
              <p className="text-xs text-gray-500 mt-1">This is where your earnings will be sent</p>
            </div>

            {/* Tax Information - EIN/SSN */}
            <div className="pt-4 border-t">
              <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                <CreditCard size={18} /> Tax Information
              </h3>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-3">
                <p className="text-sm text-blue-800">
                  <strong>Required for 1099 Tax Reporting:</strong> Enter your full EIN or Social Security Number.
                  This information is stored securely and used only for tax purposes.
                </p>
              </div>
              {profile?.ein_ssn_masked && (
                <div className="mb-3 p-2 bg-green-50 border border-green-200 rounded-lg">
                  <p className="text-sm text-green-700 flex items-center gap-2">
                    <CheckCircle size={14} />
                    Current: {profile.ein_ssn_masked} (masked for security)
                  </p>
                </div>
              )}
              <div>
                <Label className="text-gray-700 mb-1.5">
                  {profile?.ein_ssn_masked ? 'Update EIN/SSN' : 'EIN or Social Security Number *'}
                </Label>
                <Input
                  name="ein_ssn"
                  value={formData.ein_ssn}
                  onChange={(e) => {
                    const value = e.target.value.replace(/[^0-9-]/g, '').slice(0, 11);
                    setFormData({...formData, ein_ssn: value});
                  }}
                  placeholder={profile?.ein_ssn_masked ? "Enter new EIN/SSN to update" : "XX-XXXXXXX or XXX-XX-XXXX"}
                  className="border-2 focus:border-amber-500"
                />
                <p className="text-xs text-gray-500 mt-1">
                  {profile?.ein_ssn_masked 
                    ? "Leave blank to keep current value, or enter new 9-digit EIN/SSN to update"
                    : "Enter full 9-digit EIN (XX-XXXXXXX) or SSN (XXX-XX-XXXX)"
                  }
                </p>
              </div>
            </div>

            {/* Driver License Photo */}
            <div className="pt-4 border-t">
              <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                <Image size={18} /> Driver's License Photo
              </h3>
              <div className="border-2 border-dashed border-gray-300 rounded-xl p-4">
                {licensePhoto ? (
                  <div className="text-center">
                    <img 
                      src={licensePhoto} 
                      alt="Driver License" 
                      className="max-h-40 mx-auto rounded-lg border shadow-sm"
                    />
                    <p className="text-sm text-green-600 mt-2 flex items-center justify-center gap-1">
                      <CheckCircle size={14} /> License photo uploaded
                    </p>
                    <label className="cursor-pointer">
                      <span className="text-sm text-amber-600 hover:underline">Click to change</span>
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => handleUploadLicensePhoto(e.target.files[0])}
                      />
                    </label>
                  </div>
                ) : (
                  <label className="cursor-pointer block text-center">
                    {isUploadingLicense ? (
                      <div className="flex items-center justify-center gap-2 text-amber-600 py-4">
                        <Loader2 size={20} className="animate-spin" />
                        <span>Uploading...</span>
                      </div>
                    ) : (
                      <>
                        <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-2">
                          <Upload size={24} className="text-gray-400" />
                        </div>
                        <p className="text-sm text-gray-600">Upload your driver's license</p>
                        <p className="text-xs text-gray-400">PNG, JPG (max 5MB)</p>
                      </>
                    )}
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => handleUploadLicensePhoto(e.target.files[0])}
                      disabled={isUploadingLicense}
                    />
                  </label>
                )}
              </div>
            </div>

            {/* Vehicle Info */}
            <div className="pt-4 border-t">
              <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                <Car size={18} /> Vehicle Information
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-700 mb-1.5">Vehicle Type</Label>
                  <Input
                    name="vehicle_type"
                    value={formData.vehicle_type}
                    onChange={handleChange}
                    placeholder="Sedan"
                    className="border-2 focus:border-amber-500"
                  />
                </div>
                <div>
                  <Label className="text-gray-700 mb-1.5">Model</Label>
                  <Input
                    name="vehicle_model"
                    value={formData.vehicle_model}
                    onChange={handleChange}
                    placeholder="Toyota Camry"
                    className="border-2 focus:border-amber-500"
                  />
                </div>
                <div>
                  <Label className="text-gray-700 mb-1.5">Year</Label>
                  <Input
                    name="vehicle_year"
                    value={formData.vehicle_year}
                    onChange={handleChange}
                    placeholder="2022"
                    className="border-2 focus:border-amber-500"
                  />
                </div>
                <div>
                  <Label className="text-gray-700 mb-1.5">License Plate</Label>
                  <Input
                    name="license_plate"
                    value={formData.license_plate}
                    onChange={handleChange}
                    placeholder="ABC 1234"
                    className="border-2 focus:border-amber-500"
                  />
                </div>
              </div>
            </div>

            {/* Vehicle Photos Section */}
            <div className="pt-4 border-t">
              <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                <Image size={18} /> Vehicle Photos (with visible license plate)
              </h3>
              <p className="text-xs text-gray-500 mb-4">
                Upload clear photos of your vehicle's front and rear with the license plate tag visible
              </p>
              
              <div className="grid grid-cols-2 gap-4">
                {/* Front Photo */}
                <div className="space-y-2">
                  <Label className="text-gray-700">Front of Vehicle</Label>
                  <div className="relative">
                    {vehiclePhotos.front ? (
                      <div className="relative group">
                        <img 
                          src={vehiclePhotos.front.startsWith('/') ? `${backendUrl}${vehiclePhotos.front}` : vehiclePhotos.front}
                          alt="Vehicle Front" 
                          className="w-full h-32 object-cover rounded-lg border-2 border-green-300"
                        />
                        <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center">
                          <label className="cursor-pointer">
                            <input
                              type="file"
                              accept="image/*"
                              className="hidden"
                              onChange={(e) => handleUploadVehiclePhoto('front', e.target.files[0])}
                              disabled={isUploadingFront}
                            />
                            <span className="text-white text-sm flex items-center gap-1">
                              <Camera size={16} /> Change
                            </span>
                          </label>
                        </div>
                        <div className="absolute top-1 right-1 bg-green-500 rounded-full p-1">
                          <CheckCircle size={12} className="text-white" />
                        </div>
                      </div>
                    ) : (
                      <label className="cursor-pointer">
                        <input
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={(e) => handleUploadVehiclePhoto('front', e.target.files[0])}
                          disabled={isUploadingFront}
                        />
                        <div className="w-full h-32 border-2 border-dashed border-gray-300 rounded-lg flex flex-col items-center justify-center hover:border-amber-500 transition-colors">
                          {isUploadingFront ? (
                            <Loader2 className="animate-spin text-amber-500" size={24} />
                          ) : (
                            <>
                              <Upload size={24} className="text-gray-400 mb-2" />
                              <span className="text-xs text-gray-500">Upload Front</span>
                            </>
                          )}
                        </div>
                      </label>
                    )}
                  </div>
                </div>

                {/* Rear Photo */}
                <div className="space-y-2">
                  <Label className="text-gray-700">Rear of Vehicle (Tag Visible)</Label>
                  <div className="relative">
                    {vehiclePhotos.rear ? (
                      <div className="relative group">
                        <img 
                          src={vehiclePhotos.rear.startsWith('/') ? `${backendUrl}${vehiclePhotos.rear}` : vehiclePhotos.rear}
                          alt="Vehicle Rear" 
                          className="w-full h-32 object-cover rounded-lg border-2 border-green-300"
                        />
                        <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center">
                          <label className="cursor-pointer">
                            <input
                              type="file"
                              accept="image/*"
                              className="hidden"
                              onChange={(e) => handleUploadVehiclePhoto('rear', e.target.files[0])}
                              disabled={isUploadingRear}
                            />
                            <span className="text-white text-sm flex items-center gap-1">
                              <Camera size={16} /> Change
                            </span>
                          </label>
                        </div>
                        <div className="absolute top-1 right-1 bg-green-500 rounded-full p-1">
                          <CheckCircle size={12} className="text-white" />
                        </div>
                      </div>
                    ) : (
                      <label className="cursor-pointer">
                        <input
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={(e) => handleUploadVehiclePhoto('rear', e.target.files[0])}
                          disabled={isUploadingRear}
                        />
                        <div className="w-full h-32 border-2 border-dashed border-gray-300 rounded-lg flex flex-col items-center justify-center hover:border-amber-500 transition-colors">
                          {isUploadingRear ? (
                            <Loader2 className="animate-spin text-amber-500" size={24} />
                          ) : (
                            <>
                              <Upload size={24} className="text-gray-400 mb-2" />
                              <span className="text-xs text-gray-500">Upload Rear</span>
                            </>
                          )}
                        </div>
                      </label>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Save Button */}
            <div className="flex gap-3 pt-4">
              <Button 
                variant="outline" 
                onClick={onClose}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button 
                onClick={handleSave}
                disabled={isSaving}
                className="flex-1 bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-600 hover:to-yellow-600"
              >
                {isSaving ? (
                  <>
                    <Loader2 className="animate-spin mr-2" size={18} />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="mr-2" size={18} />
                    Save Changes
                  </>
                )}
              </Button>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
};

export default DriverProfileEdit;
